import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

import PageTitle from "../../layouts/PageTitle";
import { selectUserRole } from "../../../store/authSlice";
import {
  ptGetTask,
  ptUpdateTaskStatus,
} from "../../../services/personalTasks";
import {
  STATUSES,
  fmtDateTime,
  fmtDayLabel,
  priorityBadge,
  statusBadge,
  taskDate,
} from "./taskHelpers";

// ─────────────────────────────────────────────────────────────────────────────
// Task Detail (/tasks/:id) — full view of one personal task: details, a stage
// update (status + remark), and the complete status history.
// ─────────────────────────────────────────────────────────────────────────────

const TaskDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const role = useSelector(selectUserRole);
  const isSuperAdmin = role === "super-admin";

  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({ status: "Pending", remarks: "" });

  const load = () => {
    setLoading(true);
    ptGetTask(id)
      .then((r) => {
        const t = r.data?.task;
        setTask(t);
        setDraft({ status: t?.status || "Pending", remarks: "" });
        setNotFound(!t);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isSuperAdmin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isSuperAdmin]);

  const handleUpdate = async () => {
    setSaving(true);
    try {
      await ptUpdateTaskStatus(task._id, draft);
      load();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Could not update status",
        text: err?.response?.data?.message || "Something went wrong.",
      });
    } finally {
      setSaving(false);
    }
  };

  const title = <PageTitle activeMenu="Task Detail" motherMenu="Reports" />;

  if (!isSuperAdmin) {
    return (
      <>
        {title}
        <div className="card">
          <div className="card-body text-center text-muted py-5">
            This page is available to super-admin only.
          </div>
        </div>
      </>
    );
  }

  if (loading && !task) {
    return (
      <>
        {title}
        <div className="text-center text-muted py-5">Loading…</div>
      </>
    );
  }

  if (notFound) {
    return (
      <>
        {title}
        <div className="card">
          <div className="card-body text-center text-muted py-5">
            Task not found.{" "}
            <button
              className="btn btn-link p-0"
              onClick={() => navigate("/tasks/work-list")}
            >
              Back to Work List
            </button>
          </div>
        </div>
      </>
    );
  }

  const history = Array.isArray(task.statusHistory) ? task.statusHistory : [];
  const unchanged = draft.status === task.status && !draft.remarks.trim();

  return (
    <>
      {title}

      <div className="mb-3 d-flex gap-2">
        <button
          className="btn btn-sm btn-outline-secondary"
          onClick={() => navigate("/tasks/work-list")}
        >
          ← Work List
        </button>
        <button
          className="btn btn-sm btn-outline-secondary"
          onClick={() => navigate("/tasks")}
        >
          My Tasks
        </button>
      </div>

      <div className="card mb-3">
        <div className="card-header d-flex flex-wrap align-items-center gap-2">
          <h4 className="card-title mb-0 me-auto">{task.title}</h4>
          <span className={statusBadge(task.status)}>{task.status}</span>
          <span className={priorityBadge(task.priority)}>{task.priority}</span>
          {task.recurring === "Daily" && (
            <span className="badge bg-info text-dark">Daily</span>
          )}
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-sm-4">
              <div className="small text-muted">Date</div>
              <div className="fw-semibold">{fmtDayLabel(taskDate(task))}</div>
            </div>
            <div className="col-sm-4">
              <div className="small text-muted">Due</div>
              <div>{task.dueAt ? fmtDateTime(task.dueAt) : "—"}</div>
            </div>
            <div className="col-sm-4">
              <div className="small text-muted">Created</div>
              <div>{fmtDateTime(task.createdAt)}</div>
            </div>
            {task.completedAt && (
              <div className="col-sm-4">
                <div className="small text-muted">Completed</div>
                <div>{fmtDateTime(task.completedAt)}</div>
              </div>
            )}
            <div className="col-12">
              <div className="small text-muted">Description</div>
              <div style={{ whiteSpace: "pre-wrap" }}>
                {task.description || "—"}
              </div>
            </div>
          </div>

          <hr />

          <div className="row g-2 align-items-end">
            <div className="col-sm-3">
              <label className="form-label small mb-1">Status</label>
              <select
                className="form-select form-select-sm"
                value={draft.status}
                onChange={(e) =>
                  setDraft((p) => ({ ...p, status: e.target.value }))
                }
              >
                {STATUSES.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="col-sm-7">
              <label className="form-label small mb-1">Remark</label>
              <input
                className="form-control form-control-sm"
                placeholder="Remark for this stage (optional)"
                value={draft.remarks}
                onChange={(e) =>
                  setDraft((p) => ({ ...p, remarks: e.target.value }))
                }
              />
            </div>
            <div className="col-sm-2 d-grid">
              <button
                className="btn btn-sm btn-primary"
                disabled={saving || unchanged}
                onClick={handleUpdate}
              >
                Update
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h4 className="card-title mb-0">History ({history.length})</h4>
        </div>
        <div className="card-body p-0">
          {history.length === 0 ? (
            <div className="text-center text-muted py-4">No history yet.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-bordered mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {[...history].reverse().map((entry, index) => (
                    <tr key={index}>
                      <td className="text-nowrap">
                        {fmtDateTime(entry.changedAt)}
                      </td>
                      <td>
                        <span className={statusBadge(entry.status)}>
                          {entry.status}
                        </span>
                      </td>
                      <td>{entry.remarks || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default TaskDetail;
